import { hasKrPurchaseCapability } from '@entities/kr-payment/api'
import { podoFetcher } from '@server/infrastructure/external-apis/podo/client'
import { BaseResponseSchema } from '@server/schemas'

import { getBearerToken } from '../../authentication'

// Filters API Response Types
export type LangItem = { langType: string; display: string }
export type ProductItem = { product: string; displayName: string; description: string; badge: string | null }
export type PeriodItem = { period: number; displayName: string; badge: string | null }

export type SubscribeFiltersResponse = {
  langList: LangItem[]
  productList: ProductItem[]
  periodList: PeriodItem[]
}

// List API Response Types
export type AppliedCoupon = {
  couponId: string
  title: string
  discountType: 'FIXED' | 'PERCENTAGE'
  discountAmount: number
  discountAmountMax: number
  discountApplicationType?: 'RECURRING' | 'LIMITED'
  discountedPaymentCount?: number
}

export type SubscribeTicketV2 = {
  id: string
  productId?: string | null
  currency?: string | null
  paymentAmount?: number | null
  finalPaymentAmount?: number | null
  productKind?: string | null
  totalLessonCount?: number | null
  validityMonths?: number | null
  displayOrder?: number | null
  countryCode?: string | null
  langType: string
  subName: string
  lessonMonth: number
  lessonCountPerMonth: number
  originPrice: number
  subPrice: number
  viewPrice: number
  viewDiscountRate: number
  finalPrice: number
  finalDiscountRate: number
  paymentType: 'LUMP_SUM' | 'SUBSCRIBE' | 'IPAD'
  lessonTime: number
  curriculumType: string
  promotionType: string
  appliedCoupon: AppliedCoupon | null
  description: string[]
  eventDescription: string[]
}

// API Functions
export const getSubscribeFilters = async (useKrProductCatalog = false) => {
  return await podoFetcher<BaseResponseSchema<SubscribeFiltersResponse>>({
    url: `/api/v1/subscribe/meta/filters${useKrProductCatalog && (await hasKrPurchaseCapability()) ? '?catalogProtocol=KR_PRODUCT_V1' : ''}`,
    method: 'GET',
  })
}

export type GetSubscribeListParams = {
  langType: string
  lessonTime?: string
  curriculumType: string
  lessonCountPerMonth?: string
  paymentType?: string | null
  promotionType?: string
}

// 비로그인 회원용 API (appliedCoupon: null)
export const getSubscribeListNoAuth = async (params: GetSubscribeListParams, useKrProductCatalog = false) => {
  const searchParams = new URLSearchParams()
  if (useKrProductCatalog && (await hasKrPurchaseCapability())) {
    searchParams.set('catalogProtocol', 'KR_PRODUCT_V1')
  }
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.set(key, value)
    }
  })

  const response = await podoFetcher<BaseResponseSchema<SubscribeTicketV2[]>>({
    url: `/api/v1/subscribe/meta/list/no-auth?${searchParams.toString()}`,
    method: 'GET',
  })
  return response
}

// 로그인 회원용 API (appliedCoupon 포함)
export const getSubscribeList = async (
  params: GetSubscribeListParams,
  bearerToken?: string,
  useKrProductCatalog = false,
) => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const searchParams = new URLSearchParams()
  if (useKrProductCatalog && (await hasKrPurchaseCapability(authorizationToken))) {
    searchParams.set('catalogProtocol', 'KR_PRODUCT_V1')
  }
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.set(key, value)
    }
  })

  const response = await podoFetcher<BaseResponseSchema<SubscribeTicketV2[]>>({
    url: `/api/v1/subscribe/meta/list?${searchParams.toString()}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })
  return response
}
